import { faLayerGroup, faUpload } from '@fortawesome/free-solid-svg-icons';
import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapProvider } from 'react-map-gl';
import { Loader, MapMenuItem } from 'vio-components';
import { useReduxState } from 'vio-libs';

import { FiberMenu, Map as MapComponent, PreviewMenu } from '../components';
import { IMapFeatureSource, IMapFeaturesState, IPreviewFiber } from '../interfaces';
import { addPreviewFile as addPreviewFileAction, requestSources } from '../redux';
import { useAppDispatch } from '../redux/hooks';

import 'mapbox-gl/dist/mapbox-gl.css';

const Map: React.FC = (): JSX.Element => {
	// HOOKS
	const dispatch = useAppDispatch();
	const { t } = useTranslation();
	// REDUX STATE
	const {
		previewFile: activePreviewFile,
		requestingSources,
		sources
	} = useReduxState<IMapFeaturesState>('mapFeatures');
	// LOCAL STATE
	const [ showFiberMenuTooltip, setShowFiberMenuTooltip ] = useState(false);
	const [ showPreviewTooltip, setShowPreviewTooltip ] = useState(false);
	const [ showFiberPanel, setShowFiberPanel ] = useState(false);
	const [ showPreviewMenu, setShowPreviewMenu ] = useState(!!activePreviewFile);
	const [ hiddenSources, setHiddenSources ] = useState<Array<IMapFeatureSource>>([]);
	const [ previewFiber, setPreviewFiber ] = useState<IPreviewFiber['geoJSON']>(null);
	// REDUX ACTIONS
	const requestSourcesDispatch = bindActionCreators(requestSources, dispatch);
	const addPreviewFileDispatch = bindActionCreators(addPreviewFileAction, dispatch);

	const resetHiddenSources = () => {
		setHiddenSources([]);
	};

	const toggleSourceVisibility = (source: IMapFeatureSource, sources: Array<IMapFeatureSource> = null) => {
		if (Array.isArray(sources)) {
			setHiddenSources([ ...sources ]);
		}
		else {
			if (!hiddenSources.includes(source)) {
				setHiddenSources([ ...hiddenSources, source ]);
			}
			else {
				setHiddenSources(hiddenSources.filter((hiddenSource) => hiddenSource !== source));
			}
		}
	};

	const togglePreviewMenu = ()=>{
		setShowPreviewMenu(!showPreviewMenu);
		setShowFiberPanel(false);
	};

	const toggleFiberMenu = ()=>{
		setShowPreviewMenu(false);
		setShowFiberPanel(!showFiberPanel);
	};

	const addPreviewFile = (previewFiber: IPreviewFiber) => {
		addPreviewFileDispatch(previewFiber);
	};

	useEffect(() => {
		requestSourcesDispatch();
	}, []);

	if (requestingSources) {
		return (
			<div id='map-view'>
				<Loader message={ `Loading ${ t('MAP') }...` }/>
			</div>
		);
	}

	return (
		<div id='map-view'>
			<MapProvider>
				<MapComponent
					sources={ sources }
					hiddenSources={ hiddenSources }
					previewFiber={ previewFiber } />

				<div className='menu-navigation-bar'>
					<MapMenuItem
						active={ showFiberPanel }
						icon={ faLayerGroup }
						onClick={ toggleFiberMenu }
						onMouseEnter={ () => setShowFiberMenuTooltip(true) }
						onMouseLeave={ () => setShowFiberMenuTooltip(false) }
						tooltipAlign={ 'left' }
						tooltipInactive={ showFiberPanel || showPreviewMenu }
						tooltipMessage='Manage fiber visibility'
						tooltipVisible={ showFiberMenuTooltip } />

					<MapMenuItem
						active={ showPreviewMenu }
						className='preview'
						icon={ faUpload }
						onClick={ togglePreviewMenu }
						onMouseEnter={ () => setShowPreviewTooltip(true) }
						onMouseLeave={ () => setShowPreviewTooltip(false) }
						tooltipAlign={ 'left' }
						tooltipInactive={ showPreviewMenu || showFiberPanel }
						tooltipMessage='Manage fiber preview'
						tooltipVisible={ showPreviewTooltip } />
				</div>

				{ showFiberPanel && <FiberMenu
					hiddenSources={ hiddenSources }
					sources={ sources }
					toggleVisibility={ toggleSourceVisibility }
					resetHiddenSources={ resetHiddenSources } /> }

				{ showPreviewMenu && <PreviewMenu
					activePreviewFile={ activePreviewFile }
					addPreviewFiber={ setPreviewFiber }
					addPreviewFile={ addPreviewFile } /> }
			</MapProvider>
		</div>
	);
};

export default Map;
